import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import getPillarLocalDevelopment from '@jira/ssr__global-build-context/src/getPillarLocalDevelopment.tsx';
import type { JsonLd } from '@atlaskit/json-ld-types/jsonld';
import type { ProductType } from '@atlaskit/linking-common/types';
// eslint-disable-next-line jira/restricted/fedramp-isolated-cloud
import { isFedRamp } from '@atlassian/atl-context';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fg } from '@atlassian/jira-feature-gating';

import {
	groupSmartLinksByHostname,
	isFirstPartyLink,
	mergeSmallThirdPartyBatches,
	splitLinksIntoBatches,
	type SmartLinkType,
} from './smartLinkBatchHelpers.tsx';

// SmartLinks URLs
const SMART_LINKS_STAGE_URL = 'https://object-resolver.staging.atl-paas.net/resolve/batch';
const SMART_LINKS_PROD_URL = 'https://object-resolver.prod.atl-paas.net/resolve/batch';
export const SMART_LINK_INDIVIDUAL_TIMEOUT: number = 300;
export const SMART_LINK_BATCH_TIMEOUT: number = SMART_LINK_INDIVIDUAL_TIMEOUT + 100;
export const DEFAULT_MIN_BATCH_COUNT: number = 1;
export const DEFAULT_MAX_BATCH_COUNT: number = 50;

type OrsBatchResponseItem = {
	body?: JsonLd.Response;
	status?: number;
};

export type PrefetchedSmartlinkData = {
	url: string;
	body: JsonLd.Response;
};

type EnvironmentType = 'dev' | 'staging' | 'prod' | '';

// global variables will only show up on GlobalThis with a var declaration
declare global {
	// eslint-disable-next-line vars-on-top
	var ssrContext:
		| {
				entryScriptVersionId: string;
				environmentType: EnvironmentType;
				environment: string;
				awsRegion: string;
				runtimeImageVersion?: string;
				isInFedramp: boolean;
				isInIC: boolean;
				icName?: string;
		  }
		| undefined;
}

function getProductForSmartCardProvider(projectType?: ProjectType): ProductType | undefined {
	switch (projectType) {
		case 'software':
			return 'JSW';
		case 'business':
			return 'JWM';
		case 'service_desk':
			return 'JSM';
		case 'product_discovery':
			return 'JPD';
		default:
			return undefined;
	}
}

export function getHostOrigin(): string {
	// Get the original URL from the request header
	const originalUrl = globalThis.location.origin;
	const url = new URL(originalUrl?.startsWith('http') ? originalUrl : `https://${originalUrl}`);
	const host = url.origin; // This will be "https://abc.jira-dev.com"

	return host;
}

export function getSSRAuthHeaders(
	projectType?: ProjectType,
	cloudId?: string,
): Record<string, string> {
	const isLocal = getPillarLocalDevelopment();

	const xProduct = getProductForSmartCardProvider(projectType);

	// Extract the origin (e.g., https://abc.jira-dev.com)
	const host = getHostOrigin();

	return {
		Accept: 'application/json',
		'Content-Type': 'application/json',
		...(host ? { Origin: host?.startsWith('http') ? host : `https://${host}` } : {}),
		...(cloudId ? { CloudId: cloudId } : {}),
		...(xProduct ? { 'X-Product': xProduct } : {}),
		...(!isLocal ? { 'x-ssr-service-name': 'object-resolver-service' } : {}),
	};
}

function toConsumableSmartlinkData(
	urls: string[],
	orsResponse: OrsBatchResponseItem[] | null,
): PrefetchedSmartlinkData[] | null {
	if (!Array.isArray(orsResponse) || urls.length !== orsResponse.length) {
		return null;
	}
	const entries: PrefetchedSmartlinkData[] = [];
	for (let i = 0; i < urls.length; i++) {
		const item = orsResponse[i];
		if (item?.status === 200 && item?.body) {
			entries.push({ url: urls[i], body: item.body });
		}
	}
	return entries.length > 0 ? entries : null;
}

export function getORSUrl(operationName: string): string {
	const isLocal = getPillarLocalDevelopment();
	const isProd = globalThis.ssrContext?.environmentType === 'prod';

	if (isLocal) {
		return `/gateway/api/object-resolver/resolve/batch?operation=${operationName}`;
	}

	if (isProd) {
		return `${SMART_LINKS_PROD_URL}?operation=${operationName}`;
	}

	return `${SMART_LINKS_STAGE_URL}?operation=${operationName}`;
}

export function canCallORS(): boolean {
	if (!__SERVER__ && fg('dfo_issue_view_remote_data_srr_group')) {
		return false;
	}
	if (fg('jiv_22691_ors_ssr_is_fedramp_check')) {
		return !isFedRamp();
	}
	return !globalThis?.ssrContext?.isInFedramp;
}

async function fetchORSBatch(
	batch: SmartLinkType[],
	headers: Record<string, string>,
	orsUrl: string,
	batchTimeout: number,
): Promise<PrefetchedSmartlinkData[]> {
	const urls = batch.map(({ resourceUrl }) => resourceUrl);
	const orsBody = batch.map(({ resourceUrl }) => ({ resourceUrl }));

	const task = async (): Promise<PrefetchedSmartlinkData[]> => {
		try {
			// eslint-disable-next-line jira/js/no-jira-fetch
			const resolved = await performPostRequest<OrsBatchResponseItem[]>(orsUrl, {
				headers,
				body: JSON.stringify(orsBody),
				method: 'POST',
			});
			return toConsumableSmartlinkData(urls, resolved) ?? [];
		} catch {
			return [];
		}
	};

	return new Promise<PrefetchedSmartlinkData[]>((resolve) => {
		const timer = setTimeout(() => resolve([]), batchTimeout);
		task().then((result) => {
			clearTimeout(timer);
			resolve(result);
		});
	});
}

export async function fetchORSDataSSR(
	urls: string[],
	operationName: string,
	projectType?: ProjectType,
	cloudId?: string,
): Promise<PrefetchedSmartlinkData[] | null> {
	const smartLinks: SmartLinkType[] = urls.map((url) => ({ resourceUrl: url }));

	const headers = getSSRAuthHeaders(projectType, cloudId);
	// Pass individual timeout as a hint to ORS on how long it should spend per link
	const orsUrl = `${getORSUrl(operationName)}&timeout=${SMART_LINK_INDIVIDUAL_TIMEOUT}`;

	let batches = groupSmartLinksByHostname(smartLinks);
	batches = mergeSmallThirdPartyBatches(batches, DEFAULT_MIN_BATCH_COUNT);
	batches = splitLinksIntoBatches(batches, DEFAULT_MAX_BATCH_COUNT);

	// Fire first-party batches before third-party so slow third-party timeouts
	// don't prevent faster first-party links from resolving
	const firstPartyBatches = batches.filter((batch) => isFirstPartyLink(batch[0].resourceUrl));
	const thirdPartyBatches = batches.filter((batch) => !isFirstPartyLink(batch[0].resourceUrl));
	const orderedBatches = [...firstPartyBatches, ...thirdPartyBatches];

	const results = await Promise.all(
		orderedBatches.map((batch) => fetchORSBatch(batch, headers, orsUrl, SMART_LINK_BATCH_TIMEOUT)),
	);

	const allResults = results.flat();
	return allResults.length > 0 ? allResults : null;
}
